import { Component, ViewEncapsulation, OnDestroy, HostBinding } from '@angular/core';
import { toString as luceneToString } from 'lucene';
import { map, catchError, of, Subscription } from 'rxjs';
import { getCurrencySymbol, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { QualityIssueService } from '../../../services/quality-issue.service';
import { QualityIssueFilterService } from '../../../services/quality-issue-filter.service';
import { PaginatedResult } from '../../../../shared/models/paginated-result';
import { QualitySum } from '../../../models/quality-sum';
import { TranslationService } from '../../../../shared/services/translation.service';
import { formatNumber } from '../../../../shared/utils/number.format';
import { ClaimStatus, CLOSED_STATUS_LIST, PENDING_STATUS_LIST } from '../../../models/quality-issue';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';

import { QualityFeedback } from './quality-feedback.model';
import { QualityFeedbackBarsComponent } from './quality-feedback-bars.component';

@Component({
	selector: 'copo-quality-feedback-card',
	templateUrl: './quality-feedback-card.component.html',
	styleUrl: './quality-feedback-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		NgIf,

		MatButtonModule,
		RouterLink,
		QualityFeedbackBarsComponent,
		MatIconModule,
		LoadingSpinnerComponent
	]
})

export class QualityFeedbackCardComponent implements OnDestroy {
	@HostBinding('class.card-block') block = true;
	widgetData: QualityFeedback[];
	openIssues = 0;
	closedIssues = 0;
	closedSub?: Subscription;
	pendingSub?: Subscription;
	paidSub?: Subscription;
	rejectedSub?: Subscription;
	paidAmountSub?: Subscription;
	rejectedAmountSub?: Subscription;
	howManyLoaded = 0;

	constructor(
		private readonly qualityIssueService: QualityIssueService,
		private readonly qualityIssueFilterService: QualityIssueFilterService,
		private readonly translationService: TranslationService
	) {
		this.howManyLoaded = 0;
		const paidType = this.translationService.getTR('quality.feedback.card.closed.paid');
		const rejectedType = this.translationService.getTR('quality.feedback.card.closed.rejected');

		this.widgetData = [
			{
				amount: '0',
				total: 0,
				type: paidType,
				redirect: '/quality-issues/list',
				status: ClaimStatus.PAID
			},
			{
				amount: '0',
				total: 0,
				type: rejectedType,
				redirect: '/quality-issues/list',
				status: ClaimStatus.REFUSED
			}
		];

		this.getPendingTotal();
		this.getClosedTotal();
		this.getPaidTotal();
		this.getRejectedTotal();
		this.getPaidAmount();
		this.getRejectedAmount();
	}

	ngOnDestroy() {
		this.pendingSub?.unsubscribe();
		this.closedSub?.unsubscribe();
		this.paidSub?.unsubscribe();
		this.rejectedSub?.unsubscribe();
		this.paidAmountSub?.unsubscribe();
		this.rejectedAmountSub?.unsubscribe();
	}

	viewDetailsState(pending: boolean) {
		const filter: (
			{
				id: string;
				values: {
					apiName: string;
					selected: boolean;
					value: string;
				}[][];
			}
		)[] = [
			{
				id: 'status',
				values: [
					[
						ClaimStatus.DRAFT,
						ClaimStatus.PENDING,
						ClaimStatus.CREATED,
						ClaimStatus.CANCELLED,
						ClaimStatus.PAID,
						ClaimStatus.REFUSED,
						ClaimStatus.REGWARNING
					].map(status => ({
						apiName: 'status',
						selected: (pending ? PENDING_STATUS_LIST : CLOSED_STATUS_LIST).includes(status),
						value: status
					}))
				]
			}
		];

		return {
			filter
		};
	}

	private getRejectedAmount() {
		this.rejectedAmountSub = this.getAmount('amountRequested', 'currencyRequested', 'rejected').subscribe(
			{
				next: res => {
					this.widgetData[1].amount = res;
					this.howManyLoaded++;
				}
			}
		);
	}

	private getPaidAmount() {
		this.paidAmountSub = this.getAmount('amountAcceptedInEuro', 'currencyAccepted', 'paid').subscribe(
			{
				next: res => {
					this.widgetData[0].amount = res;
					this.howManyLoaded++;
				}
			}
		);
	}

	private getRejectedTotal() {
		this.rejectedSub = this.getClosedCount('rejected').subscribe(
			{
				next: res => {
					this.widgetData[1].total = res;
					this.howManyLoaded++;
				}
			}
		);
	}

	private getPaidTotal() {
		this.paidSub = this.getClosedCount('paid').subscribe(
			{
				next: res => {
					this.widgetData[0].total = res;
					this.howManyLoaded++;
				}
			}
		);
	}

	private getClosedTotal() {
		this.closedSub = this.getCount(['closed']).subscribe({
			next: res => {
				this.closedIssues = res;
				this.howManyLoaded++;
			},
			error: (error: unknown) => {
				console.error(error);
				this.closedIssues = 0;
				this.howManyLoaded++;
			}
		});
	}

	private getPendingTotal() {
		this.pendingSub = this.getCount(['pending']).subscribe({
			next: (res: number) => {
				this.openIssues = res;
				this.howManyLoaded++;
			},
			error: (error: unknown) => {
				console.error(error);
				this.openIssues = 0;
				this.howManyLoaded++;
			}
		});
	}

	private getCount(claimStatusList: string[]) {
		return this.qualityIssueService.getIssuesCount(
			luceneToString(this.qualityIssueFilterService.createDefaultFilters(new Set(claimStatusList)))
		).pipe(
			map(res => res.count),
			catchError(
				(err: unknown) => {
					console.error('error retrieving issues', err);
					return of(0);
				}
			)
		);
	}

	private getClosedCount(claimStatus: string) {
		return this.qualityIssueService.getIssuesCount(
			luceneToString(
				claimStatus === 'paid'
					? this.qualityIssueFilterService.createPaidFilter()
					: this.qualityIssueFilterService.createRefusedFilter()
			)
		).pipe(
			map(res => res.count),
			catchError(
				(err: unknown) => {
					console.error('error retrieving closed issues', err);
					return of(0);
				}
			)
		);
	}

	private getAmount(field: string, groupBy: string, claimStatus: string) {
		return this.qualityIssueService.getIssuesSum(
			field,
			groupBy,
			luceneToString(
				claimStatus === 'paid'
					? this.qualityIssueFilterService.createPaidFilter()
					: this.qualityIssueFilterService.createRefusedFilter()
			)
		).pipe(
			map(res => this.getAmountWithCurrency(res)),
			catchError(
				(err: unknown) => {
					console.error('error retrieving issues sum', err);
					return of('0');
				}
			)
		);
	}

	private getAmountWithCurrency(res: PaginatedResult<QualitySum>) {
		if (res.results.length !== 1) {
			return '0';
		}
		// eslint-disable-next-line @typescript-eslint/no-deprecated
		return formatNumber(res.results[0].sum, 'number', getCurrencySymbol(this.normalizeCurrency(res), 'narrow'));
	}

	private normalizeCurrency(res: PaginatedResult<QualitySum>) {
		if (res.results[0].currency === undefined) {
			return 'EUR';
		}
		return res.results[0].currency;
	}
}
